export interface HazardViewEvent {
    hazard_id: number
    date_visited: Date
}

export interface ContentBlockViewEvent {
    hazard_id: number
    content_block_id: number
    date_visited: Date
}

export interface HazardViewCount {
    hazard_id: number
    view_count: number
}

// Each of these values corresponds with a value in the LoggingEvent enum in ISA
export type LoggingEvent =
    | 'OPEN_APP' // The user opens the app
    | 'OPEN_PAGE' // The user goes to a specified page
    | 'OPEN_EMERGENCY_CALL_HELPER' // The user opens the Emergency Call Helper
    | 'VIEW_HAZARD' // The user views a specified hazard
    | 'VIEW_CONTENT_BLOCK' // The user views a specified content block
    | 'SEARCH' // The user submits a search query
    | 'SELECT_FILTER' // The user enables a filter in the Hazard Guide
    | 'SELECT_HAZARD_STAGE' // The user switches to a particular hazard stage (Prepare/React/Recover)
    | 'SELECT_CONTENT_BOOSTER' // The user enables a content booster for a specified hazard
    | 'ADD_BOOKMARK' // The user bookmarks a specified hazard
    | 'REMOVE_BOOKMARK' // The user removes a specified hazard from their bookmarks
    | 'OPEN_FAQ_BLOCK' // The user opens a specified FAQ block
    | 'OPEN_PRIVACY_POLICY' // The user opens the privacy policy
    | 'OPEN_TERMS_CONDITIONS' // The user views the terms and conditions
    | 'REPORT_A_BUG' // The user presses the Report a Bug button from a specified page
    | 'REPORT_TRANSLATION_ERROR' // The user presses the Report a Translation Bug from a specified page
    | 'REQUEST_HAZARD' // The user requests a hazard from a specified page
    | 'REQUEST_FEATURE' // The user reqeusts a new feature from a specified page
    | 'SUBMIT_FEEDBACK' // The user opens the Submit Feedback form
    | 'SWITCH_LANGUAGE' // The user switches to another language
    | 'OPEN_TWITTER' // The user presses the Twitter icon to go to our Twitter
    | 'OPEN_FACEBOOK' // The user presses the Facebook icon to go to our Facebook
    | 'OPEN_INSTAGRAM' // The user presses the Instagram icon to go to our Instagram
    | 'OPEN_LINKEDIN' // The user presses the LinkedIn icon to go to our LinkedIn
    | 'OPEN_WHY_HAZADAPT' // The user presses the "Why HazAdapt?" link
    | 'OPEN_WEBSITE' // The user presses the website icon to go to our website
    | 'GO_TO_APP_IN_APPLE_APP_STORE' // The user presses the Download on the App Store button in the footer
    | 'GO_TO_APP_IN_GOOGLE_PLAY' // The user presses the Get It On Google Play button in the footer
    | 'SHARE_HAZARD' // The user copies a link to a hazard
    | 'SHARE_ADDRESS' // The user shares their address from the Emergency Call Helper
    | 'OPEN_COMMUNITY_SPONSORS' // The user opens a hazard's community sponsors block
    | 'CLICK_COMMUNITY_SPONSOR_LINK' // The user opens a community sponsor's link
    | 'CLICK_COMMUNITY_SPONSORS_HELP_ICON' // The user clicks the help icon on a hazard's Community Sponsors block
    | 'HOVER_COMMUNITY_SPONSOR' // The user's mouse enters the bounding box of a Community Sponsor
    | 'CLICK_COMMUNITY_SPONSOR' // The user clicks inside of the bounding box of a Community Sponsor
    | 'CLICK_COMMUNITY_SPONSOR_SUPPLEMENTAL_LINK' // The user clicks on one of the extra links for a Community Sponsor
    | 'OPEN_HAZARD_PDF' // The user clicks on the PDF button for a hazard
    | 'SHARE_ADDRESS' // The user clicks the 'Share' button on their address in the Emergency Call Helper
    | 'SHARE_PREP_CHECK' // The user shares a prep check
    | 'CLICK_LOCO_TIER_1_LINK' // The user clicks a Tier 1 LoCo link
    | 'CLICK_HAZARD_CONTENT_LINK' // The user clicks a link in hazard content
    | 'HOVER_LOCO_TIER_1' // The user's mouse enters the bounding box of a Tier 1 content chunk
    | 'OPEN_CSO' // The user opens a CSO
    | 'OPEN_CSO_WHAT_THEY_HELP_WITH' // The user opens a CSO's "What They Help With" section
    | 'OPEN_CSO_WHO_THEY_HELP' // The user opens a CSO's "Who They Help" section
    | 'OPEN_CSO_ABOUT_THIS_SERVICE' // The user opens a CSO's "About This Service" section
    | 'CLICK_CSO_FIND_NEAR_ME' // The user clicks a CSO's "Find Near Me" button/link
    | 'CLICK_CSO_VIEW_WEBSITE' // The user clicks a CSO's "View Website" button/link
    | 'HOVER_CSO' // The user's mouse enters the space of a CSO (web)
    | 'TEXT_CSO' // The user presses the Text button for a CSO (mobile)
    | 'DIAL_CSO' // The user presses the Dial button for a CSO (mobile)
    | 'DIAL_911' // The user presses the Dial 911 button (mobile)
    | 'HOVER_HAZARD' // The user hovers over a hazard (web)
    | 'HOVER_CONTENT_BLOCK' // The user hovers over a content block (web)
    | 'OPEN_PROMOTED_PRODUCTS' // The user opens a hazard's promoted products block
    | 'CLICK_PROMOTED_PRODUCT' // The user opens a promoted product's link
    | 'CLICK_CONTACT_US' // The user clicks the contact us link in the promoted products block
    | 'CLICK_PROMOTED_PRODUCTS_HELP_ICON' // The user clicks the help icon on a hazard's Promoted Products block
    | 'HOVER_PROMOTED_PRODUCT' // The user's mouse enters the bounding box of a Promoted Product
    | 'CLICK_PROMOTED_PRODUCT_AREA' // The user clicks inside of the bounding box of a Promoted Product
    | 'CLICK_PROMOTED_PRODUCT_TEAM_MEMBER_FAVORITE' // The user clicks the Team Member Favorite icon of a Promoted Product
    | 'HOVER_PROMOTED_PRODUCTS_BLOCK' // The user hovers over the Promoted Products block
    | 'HOVER_COMMUNITY_SPONSORS_BLOCK' // The user hovers over the Community Sponsors block
    | 'SHARE_PROMOTED_PRODUCT' // The user clicks the Share button for a Promoted Product
    | 'BOOKMARK_PROMOTED_PRODUCT' // The user presses the bookmark button on a Promoted Product

export enum LoggedPage {
    ABOUT = 'About',
    ACCOUNT_DETAILS = 'AccountDetails',
    ACCOUNT = 'Account',
    BOOKMARKS = 'Bookmarks',
    CHANGE_PASSWORD = 'ChangePassword',
    CREATE_ACCOUNT = 'CreateAccount',
    EMERGENCY_CALL_HELPER = 'EmergencyCallHelper',
    FAQ = 'FAQ',
    FORGOT_PASSWORD = 'ForgotPassword',
    HAZARD_GUIDE = 'HazardGuide',
    HAZARD = 'Hazard',
    HOME = 'Home',
    INFO_CUSTOMIZATIONS = 'InfoCustomizations',
    LANGUAGES = 'Language',
    LANGUAGES_CUSTOMIZATIONS = 'LanguagesAndCustomizations',
    LOGIN = 'Login',
    MY_EMERGENCY_ITEMS = 'MyEmergencyItems',
    PREP_CHECK = 'PrepCheck',
    PREP_CHECK_LANDING = 'PrepCheckLanding',
    PREP_CHECK_RESULTS = 'PrepCheckResults',
    MY_INFO = 'MyInfo',
    REPORT_ISSUE = 'ReportIssue',
    RESET_PASSWORD = 'ResetPassword',
    SEARCH = 'Search',
    SETTINGS = 'Settings',
    WHEN_TO_CALL_911 = 'WhenToCall911',
}

/** The status of an event. */
export enum LoggingStatus {
    /** The status could not be determined. */
    Unknown = 'unknown',
    /** The event was skipped due to configuration or callbacks. */
    Skipped = 'skipped',
    /** The event was sent successfully. */
    Success = 'success',
    /** A user or device in the payload is currently rate limited and should try again later. */
    RateLimit = 'rate_limit',
    /** The sent payload was too large to be processed. */
    PayloadTooLarge = 'payload_too_large',
    /** The event could not be processed. */
    Invalid = 'invalid',
    /** A server-side error ocurred during submission. */
    Failed = 'failed',
    /** a server or client side error occuring when a request takes too long and is cancelled */
    Timeout = 'Timeout',
    /** NodeJS runtime environment error.. E.g. disconnected from network */
    SystemError = 'SystemError',
}
